import { ExifTransform } from '../interfaces/image-cropper.interfaces';

// black 2x1 JPEG, with the following meta information set:
// - EXIF Orientation: 6 (Rotated 90° CCW)
const testAutoOrientationImageURL: string =
  'data:image/jpeg;base64,/9j/4QAiRXhpZgAATU0AKgAAAAgAAQESAAMAAAABAAYAAAA' +
  'AAAD/2wCEAAEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBA' +
  'QEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQE' +
  'BAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAQEBAf/AABEIAAEAAgMBEQACEQEDEQH/x' +
  'ABKAAEAAAAAAAAAAAAAAAAAAAALEAEAAAAAAAAAAAAAAAAAAAAAAQEAAAAAAAAAAAAAAAA' +
  'AAAAAEQEAAAAAAAAAAAAAAAAAAAAA/9oADAMBAAIRAxEAPwA/8H//2Q==';

export function supportsAutomaticRotation(): Promise<boolean> {
  return new Promise((resolve: (value: boolean) => void) => {
    const img: HTMLImageElement = new Image();
    img.onload = (): void => {
      // Check if browser supports automatic image orientation:
      const supported: boolean = img.width === 1 && img.height === 2;
      resolve(supported);
    };
    img.src = testAutoOrientationImageURL;
  });
}

export function getTransformationsFromExifData(exifRotationOrBase64Image: number | string): ExifTransform {
  if (typeof exifRotationOrBase64Image === 'string') {
    exifRotationOrBase64Image = getExifRotation(exifRotationOrBase64Image);
  }
  switch (exifRotationOrBase64Image) {
    case 2:
      return { rotate: 0, flip: true };
    case 3:
      return { rotate: 2, flip: false };
    case 4:
      return { rotate: 2, flip: true };
    case 5:
      return { rotate: 1, flip: true };
    case 6:
      return { rotate: 1, flip: false };
    case 7:
      return { rotate: 3, flip: true };
    case 8:
      return { rotate: 3, flip: false };
    default:
      return { rotate: 0, flip: false };
  }
}

function getExifRotation(imageBase64: string): number {
  const view: DataView = new DataView(base64ToArrayBuffer(imageBase64));
  if (!isValidJpegHeader(view)) {
    return -2;
  }

  return findExifOrientation(view);
}

function isValidJpegHeader(view: DataView): boolean {
  return view.getUint16(0, false) === 0xffd8;
}

function findExifOrientation(view: DataView): number {
  const length: number = view.byteLength;
  let offset: number = 2;

  while (offset < length) {
    if (view.getUint16(offset + 2, false) <= 8) return -1;

    const marker: number = view.getUint16(offset, false);
    offset += 2;

    if (marker === 0xffe1) {
      const orientation: number = extractOrientationFromExif(view, offset);
      if (orientation !== -1) return orientation;
    } else if ((marker & 0xff00) !== 0xff00) {
      break;
    } else {
      offset += view.getUint16(offset, false);
    }
  }
  return -1;
}

function extractOrientationFromExif(view: DataView, offset: number): number {
  offset += 2;
  if (view.getUint32(offset, false) !== 0x45786966) {
    return -1;
  }

  offset += 6;
  const little: boolean = view.getUint16(offset, false) === 0x4949;
  offset += view.getUint32(offset + 4, little);
  const tags: number = view.getUint16(offset, little);
  offset += 2;

  for (let i: number = 0; i < tags; i++) {
    if (view.getUint16(offset + i * 12, little) === 0x0112) {
      return view.getUint16(offset + i * 12 + 8, little);
    }
  }
  return -1;
}

function base64ToArrayBuffer(imageBase64: string): ArrayBuffer {
  imageBase64 = imageBase64.replace(/^data:([^;]+);base64,/gim, '');
  const binaryString: string = atob(imageBase64);
  const len: number = binaryString.length;
  const bytes: Uint8Array = new Uint8Array(len);
  for (let i: number = 0; i < len; i++) {
    bytes[i] = binaryString.charCodeAt(i);
  }
  return bytes.buffer as ArrayBuffer;
}

function processPixel(
  data: Uint8ClampedArray,
  xx: number,
  yy: number,
  widthSource: number,
  weight: number,
  pixelWeights: { gx_r: number; gx_g: number; gx_b: number; gx_a: number; weights: number; weights_alpha: number }
): void {
  const pos_x: number = 4 * (xx + yy * widthSource);

  // Process alpha
  pixelWeights.gx_a += weight * data[pos_x + 3];
  pixelWeights.weights_alpha += weight;

  // Process colors
  let adjustedWeight: number = weight;
  if (data[pos_x + 3] < 255) {
    adjustedWeight = (weight * data[pos_x + 3]) / 250;
  }

  pixelWeights.gx_r += adjustedWeight * data[pos_x];
  pixelWeights.gx_g += adjustedWeight * data[pos_x + 1];
  pixelWeights.gx_b += adjustedWeight * data[pos_x + 2];
  pixelWeights.weights += adjustedWeight;
}

function processPixelArea(
  data: Uint8ClampedArray,
  i: number,
  j: number,
  ratioWidth: number,
  ratioHeight: number,
  ratioWidthHalf: number,
  ratioHeightHalf: number,
  widthSource: number,
  heightSource: number,
  pixelWeights: { gx_r: number; gx_g: number; gx_b: number; gx_a: number; weights: number; weights_alpha: number }
): void {
  const center_y: number = j * ratioHeight;
  const xx_start: number = Math.floor(i * ratioWidth);
  const yy_start: number = Math.floor(j * ratioHeight);
  let xx_stop: number = Math.ceil((i + 1) * ratioWidth);
  let yy_stop: number = Math.ceil((j + 1) * ratioHeight);
  xx_stop = Math.min(xx_stop, widthSource);
  yy_stop = Math.min(yy_stop, heightSource);

  for (let yy: number = yy_start; yy < yy_stop; yy++) {
    const dy: number = Math.abs(center_y - yy) / ratioHeightHalf;
    const center_x: number = i * ratioWidth;
    const w0: number = dy * dy;
    for (let xx: number = xx_start; xx < xx_stop; xx++) {
      const dx: number = Math.abs(center_x - xx) / ratioWidthHalf;
      const w: number = Math.sqrt(w0 + dx * dx);
      if (w >= 1) continue;

      const weight: number = 2 * w * w * w - 3 * w * w + 1;
      processPixel(data, xx, yy, widthSource, weight, pixelWeights);
    }
  }
}

export function resizeCanvas(canvas: HTMLCanvasElement, width: number, height: number): void {
  const width_source: number = canvas.width;
  const height_source: number = canvas.height;
  width = Math.round(width);
  height = Math.round(height);

  const ratioWidth: number = width_source / width;
  const ratioHeight: number = height_source / height;
  const ratioWidthHalf: number = Math.ceil(ratioWidth / 2);
  const ratioHeightHalf: number = Math.ceil(ratioHeight / 2);

  const ctx: CanvasRenderingContext2D | null = canvas.getContext('2d');
  if (!ctx) return;

  const img: ImageData = ctx.getImageData(0, 0, width_source, height_source);
  const img2: ImageData = ctx.createImageData(width, height);
  const { data } = img;
  const data2: Uint8ClampedArray = img2.data;

  for (let j: number = 0; j < height; j++) {
    for (let i: number = 0; i < width; i++) {
      const x2: number = (i + j * width) * 4;
      const pixelWeights = { gx_r: 0, gx_g: 0, gx_b: 0, gx_a: 0, weights: 0, weights_alpha: 0 };

      processPixelArea(
        data,
        i,
        j,
        ratioWidth,
        ratioHeight,
        ratioWidthHalf,
        ratioHeightHalf,
        width_source,
        height_source,
        pixelWeights
      );

      data2[x2] = pixelWeights.gx_r / pixelWeights.weights;
      data2[x2 + 1] = pixelWeights.gx_g / pixelWeights.weights;
      data2[x2 + 2] = pixelWeights.gx_b / pixelWeights.weights;
      data2[x2 + 3] = pixelWeights.gx_a / pixelWeights.weights_alpha;
    }
  }

  canvas.width = width;
  canvas.height = height;
  ctx.putImageData(img2, 0, 0);
}
